import { IconBox } from "components/core/IconBox";
import { SelectInterestTagModal } from "components/core/modals/SelectInterestTagModal";
import { SignInModal } from "components/core/modals/SignInModal";
import { useSessionQuery } from "graphql/generated";
import {
    CogIcon,
    EditIcon,
    EllipsisIcon,
    Settings2Icon,
    SettingsIcon
} from "lucide-react";
import { AppPage } from "models/AppPage";
import { useMemo } from "react";
import { useState } from "react";
import {
    DROPDOWN_STYLES,
    getDropdownContainerClassName,
    getDropdownContentClassName
} from "utils/navigationStyles";

import { DropdownGridLayout } from "./DropdownGridLayout";
import { DropdownListLayout } from "./DropdownListLayout";

interface NavDropdownProps {
    page: AppPage;
    currentTag?: string;
}

/**
 * Navigation dropdown container component
 * Handles the glass morphism styling and layout selection
 * Delegates to DropdownGridLayout or DropdownListLayout based on page type
 */
export const NavDropdown = ({ page, currentTag }: NavDropdownProps) => {
    const [isInterestModalOpen, setIsInterestModalOpen] = useState(false);
    const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);

    const { data: sessionData } = useSessionQuery();
    const isSignedIn = !!sessionData?.session?.me?.id;

    const isContentCuration =
        page.label === "콘텐츠 큐레이션" || page.label === "카테고리";
    const children = page.children || [];

    // Add settings item for content curation dropdown
    const allChildren = useMemo(() => {
        if (!isContentCuration) return children;

        const settingsItem = new AppPage({
            label: "설정",
            href: "#",
            external: false
        });

        // Add a custom property to identify this as settings item
        (settingsItem as any).isSettings = true;

        return [...children, settingsItem];
    }, [children, isContentCuration]);

    const handleEditClick = (e: React.MouseEvent) => {
        e.stopPropagation();

        if (isSignedIn) {
            setIsInterestModalOpen(true);
        } else {
            window.location.href = "https://mix.day/#signin";
        }
    };

    const handleSignInClose = async () => {
        setIsSignInModalOpen(false);
    };

    return (
        <>
            <div className={getDropdownContainerClassName(isContentCuration)}>
                <div
                    className={getDropdownContentClassName(isContentCuration)}
                    style={DROPDOWN_STYLES}
                >
                    {isContentCuration ? (
                        <DropdownGridLayout
                            children={allChildren}
                            currentTag={currentTag}
                            onSettingsClick={handleEditClick}
                        />
                    ) : (
                        <DropdownListLayout children={children} />
                    )}

                    {/* Subtle noise texture overlay for depth */}
                    <div
                        className="pointer-events-none absolute inset-0
                            rounded-[20px] opacity-[0.03] mix-blend-overlay"
                        style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
                        }}
                    />
                </div>
            </div>

            {/* SelectInterestTagModal - shown when user is signed in */}
            <SelectInterestTagModal
                isOpen={isInterestModalOpen}
                onRequestClose={() => setIsInterestModalOpen(false)}
            />

            {/* SignInModal - shown when user is not signed in */}
            <SignInModal
                isOpen={isSignInModalOpen}
                onRequestClose={handleSignInClose}
                close={handleSignInClose}
            />
        </>
    );
};
